import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Kein Bestätigungs-Token gefunden.');
        return;
      }

      try {
        const response = await axios.get(`${API}/api/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.message || 'E-Mail erfolgreich bestätigt!');
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage(
          error.response?.data?.detail ||
          'Ungültiger oder abgelaufener Token. Bitte fordern Sie eine neue Bestätigungs-E-Mail an.'
        );
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleContinue = () => {
    // Clear any existing token and redirect to login
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            {status === 'loading' && <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />}
            {status === 'success' && <CheckCircle className="h-8 w-8 text-green-600" />}
            {status === 'error' && <XCircle className="h-8 w-8 text-red-600" />}
          </div>
          <CardTitle className="text-2xl font-bold">
            {status === 'loading' && 'E-Mail wird bestätigt...'}
            {status === 'success' && 'Bestätigung erfolgreich!'}
            {status === 'error' && 'Bestätigung fehlgeschlagen'}
          </CardTitle>
          <CardDescription>
            {status === 'loading' && 'Bitte warten Sie einen Moment...'}
            {status === 'success' && 'Ihre E-Mail-Adresse wurde erfolgreich bestätigt.'}
            {status === 'error' && 'Es gab ein Problem bei der Bestätigung.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Status Alert */}
          {status === 'success' && (
            <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-sm text-green-900 dark:text-green-100">
                {message}
              </AlertDescription>
            </Alert>
          )}

          {status === 'error' && (
            <Alert className="border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800">
              <XCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-sm text-red-900 dark:text-red-100">
                {message}
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          {status === 'success' && (
            <div className="space-y-3">
              <Button onClick={handleContinue} className="w-full" size="lg">
                Zur Anmeldung
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Sie können sich jetzt mit Ihrem Account anmelden und AllFlowX nutzen.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <Button onClick={handleContinue} variant="outline" className="w-full" size="lg">
                Zur Anmeldung
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Versuchen Sie sich anzumelden. Falls nötig, können Sie eine neue Bestätigungs-E-Mail anfordern.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
